/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import ApiService from "./ApiService";
import Speed from "model/stats/Speed";
import SkillStats from "model/stats/SkillStats";
import Utils from "utils/Utils";
import CharacterService from "./CharacterService";
import ItemOption from 'model/character/ItemOption';
import Section from "model/abilities/Section";
import Race from "model/character/Race";
import Abilities from "model/abilities/Abilities";

class RaceService {
    static expandSubRaces(races: Race[]): Race[] {
        return races.reduce((acc, race) => {
            if (race.subraces && race.subraces.length) {
                const { subraces, ...parentAttributes } = race;
                subraces.forEach(subrace => {
                    let subraceName = subrace.name.replace(parentAttributes.name, ''); // remove 'Dwarf' from 'Hill Dwarf' and 'Elf' from 'High Elf'
                    const newSubRace: Race = {
                        ...parentAttributes,
                        ...subrace,
                        name: `${parentAttributes.name}, ${subraceName}`,
                        desc: RaceService.combineStrings(parentAttributes.desc, subrace.desc),
                        traits: RaceService.combineStrings(parentAttributes.traits, subrace.traits),
                    };
                    newSubRace.asi = [parentAttributes.asi[0], subrace.asi[0]];
                    acc.push(newSubRace);
                });
            }
            acc.push(race);
            return acc;
        }, [] as Race[]);
    }
    // Function to combine strings with a check to avoid duplicating identical content
    static combineStrings(parentStr: string, subraceStr: string): string {
        if (!parentStr) return subraceStr;
        if (!subraceStr) return parentStr;
        return parentStr === subraceStr ? parentStr : `${parentStr}\n\n${subraceStr}`;
    }

    static parseSenses(race: Race | null): string {
        let darkVision = 0;
        if (race?.vision?.includes('Darkvision')) {
            const distance = race.vision.match(/ (\d+) feet /);
            if (distance) {
                darkVision = parseInt(distance[1]);
                return `darkvision ${darkVision} ft.`;
            }
        }
        return '';
    }
    /*
     "Hunter's Senses",         "You have proficiency in the Perception and Stealth skills."
     "One With the Wilds",      "You have proficiency in one of the following skills of your choice: Insight, Medicine, Nature, or Survival."
     "Born Leader",             "You gain proficiency with two of the following skills of your choice: History, Insight, Performance, and Persuasion."
     "Clan Athlete",            "You have proficiency in the Athletics skill."
     "Clan Leader",             "You have proficiency in the Persuasion skill."
     "Clan Scout",              "You have proficiency in the Stealth skill."
     "Curiously Clever",        "You have proficiency in the Investigation skill."
     "Keen Senses",             "You have proficiency in the Perception skill."
     "Light Hooves",            "You have proficiency in the Stealth skill."
     "Menacing",                "You gain proficiency in the Intimidation skill."
     "Skill Versatility",       "You gain proficiency in two skills of your choice."
     "Tool Proficiency",        "You gain proficiency with the artisan's tools of your choice: smith's tools, brewer's supplies, or mason's tools."
     "Hardy Survivor",          "Your upbringing in mushroomfolk society has taught you how to defend yourself and find food. You have proficiency in the Survival skill."
     "Storied Culture",         "You have proficiency in the Performance skill, and you gain proficiency with one of the following musical instruments: bagpipes, drum, horn, or shawm."
     "Versatility",             "The training and experience of your early years was not lost when you became a darakhul. You have proficiency in two skills and one tool of your choice."
     
     "Athletic Training",       "You have proficiency in the Athletics skill, and you are proficient with two martial weapons of your choice."
     "Keeper of Secrets",       "You have proficiency in the Arcana skill, and you have advantage on Intelligence (Arcana) checks related to the planes and planar travel. In addition, you have proficiency in one of the following skills of your choice: History, Insight, and Religion."
     "Studied Insight",         "You are skilled at discerning other creature's motives and intentions. You have proficiency in the Insight skill, and, if you study a creature for at least 1 minute, you have advantage on any initiative checks in combat against that creature for the next hour."
     "Supernatural Senses",     "Your keen elven senses are honed even more by the power of undeath and the hunger within you. You can now smell when blood is in the air. You have proficiency in the Perception skill, and you have advantage on Wisdom (Perception) checks to notice or find a creature within 30 feet of you that doesn't have all of its hit points."
     "Adaptable Acumen",        "You gain proficiency in two skills or tools of your choice. Choose one of those skills or tools or another skill or tool proficiency you have. Your proficiency bonus is doubled for any ability check you make that uses the chosen skill or tool."
     "Tinker",                  "You have proficiency with artisan's tools (tinker's tools). Using those tools, you can spend 1 hour and 10 gp worth of materials to construct a Tiny clockwork device (AC 5, 1 hp). The device ceases to function after 24 hours (unless you spend 1 hour repairing it to keep the device functioning), or when you use your action to dismantle it; at that time, you can reclaim the materials used to create it. You can have up to three such devices active at a time.\nWhen you create a device, choose one of the following options:\n* _Clockwork Toy._ This toy is a clockwork animal, monster, or person, such as a frog, mouse, bird, dragon, or soldier. When placed on the ground, the toy moves 5 feet across the ground on each of your turns in a random direction. It makes noises as appropriate to the creature it represents.\n* _Fire Starter._ The device produces a miniature flame, which you can use to light a candle, torch, or campfire. Using the device requires your action.\n* _Music Box._ When opened, this music box plays a single song at a moderate volume. The box stops playing when it reaches the song's end or when it is closed."
     
     "Alseid Weapon Training",  "You have proficiency with spears and shortbows."
     "Dwarven Combat Training", "You have proficiency with the battleaxe, handaxe, light hammer, and warhammer."
     "Elf Weapon Training",     "You have proficiency with the longsword, shortsword, shortbow, and longbow."
     "Remembered Training",     "You remember some of your combat training from your previous life. You have proficiency with two martial weapons of your choice and with light and medium armor."
    */
    static parseSkills(race: Race | null, skillOptions: ItemOption[], skillsSelected: string[]): void {
        if (!race) {
            return;
        }
        const allSkillNames = [...SkillStats.getSkillsMap().keys()];
        for (const section of race.sections || []) {
            const desc = section.desc.toLowerCase().trim();
            const skillName = race.name + ' - ' + section.name;
            if (desc === `you have proficiency in the perception and stealth skills.`) {
                skillOptions.push({ label: skillName, options: ['Perception'] });
                skillsSelected.push('Perception');
                skillOptions.push({ label: skillName, options: ['Stealth'] });
                skillsSelected.push('Stealth');
                section.exportSection = false;
                continue;
            }
            if (desc === `you have proficiency in one of the following skills of your choice: insight, medicine, nature, or survival.`) {
                skillOptions.push({ label: skillName, options: ['Insight', 'Medicine', 'Nature', 'Survival'] });
                skillsSelected.push('');
                section.exportSection = false;
                continue;
            }
            if (desc === `you gain proficiency with two of the following skills of your choice: history, insight, performance, and persuasion.`) {
                skillOptions.push({ label: skillName, options: ['History', 'Insight', 'Performance', 'Persuasion'] });
                skillsSelected.push('');
                skillOptions.push({ label: skillName, options: ['History', 'Insight', 'Performance', 'Persuasion'] });
                skillsSelected.push('');
                section.exportSection = false;
                continue;
            }
            if (desc === `in addition, you have proficiency in one of the following skills of your choice: history, insight, and religion.`) {
                skillOptions.push({ label: skillName, options: ['History', 'Insight', 'Religion'] });
                skillsSelected.push('');
                section.exportSection = false;
                continue;
            }
            if (desc === `you gain proficiency with the artisan's tools of your choice: smith's tools, brewer's supplies, or mason's tools.`) {
                skillOptions.push({ label: skillName, options: ["Smith's tools", "Brewer's supplies", "Mason's tools"] });
                skillsSelected.push('');
                section.exportSection = false;
                continue;
            }
            if (desc === 'you gain proficiency in two skills of your choice.') {
                skillOptions.push({ label: skillName, options: [...allSkillNames] });
                skillOptions.push({ label: skillName, options: [...allSkillNames] });
                skillsSelected.push('');
                skillsSelected.push('');
                section.exportSection = false;
                continue;
            }
            if (desc.includes("you have proficiency with artisan's tools (tinker's tools).")) {
                skillOptions.push({ label: skillName, options: ["Tinker's tools"] });
                skillsSelected.push("Tinker's tools");
            }
            if (desc.includes("you gain proficiency with one of the following musical instruments: bagpipes, drum, horn, or shawm.")) {
                skillOptions.push({ label: skillName, options: ['Bagpipes', 'Drum', 'Horn', 'Shawm'] });
                skillsSelected.push('');
                section.exportSection = false;
            }
            if (desc.includes("you have proficiency in one of the following skills of your choice: history, insight, and religion.")) {
                skillOptions.push({ label: skillName, options: ['History', 'Insight', 'Religion'] });
                skillsSelected.push('');
                section.exportSection = false;
            }
            if (desc.includes('you gain proficiency in two skills or tools of your choice.')) {
                skillOptions.push({ label: skillName, options: [...allSkillNames, ...SkillStats.tools] });
                skillOptions.push({ label: skillName, options: [...allSkillNames, ...SkillStats.tools] });
                skillsSelected.push('');
                skillsSelected.push('');
            }
            if (desc.includes('you have proficiency in two skills and one tool of your choice.')) {
                skillOptions.push({ label: skillName, options: [...allSkillNames] });
                skillOptions.push({ label: skillName, options: [...allSkillNames] });
                skillOptions.push({ label: skillName, options: [...SkillStats.tools] });
                skillsSelected.push('');
                skillsSelected.push('');
                skillsSelected.push('');
                section.exportSection = false;
            }
            if (desc.includes('choose one of those skills or tools or another skill or tool proficiency you have. your proficiency bonus is doubled for any ability check you make that uses the chosen skill or tool')) {
                skillOptions.push({ label: skillName, options: [...allSkillNames, ...SkillStats.tools].map(o => o + SkillStats.EXPERT)});
                skillsSelected.push('');
            }

            for (const skill of allSkillNames) {
                if (desc === `you have proficiency in the ${skill.toLowerCase()} skill.` ||
                    desc === `you gain proficiency in the ${skill.toLowerCase()} skill.`) {
                    skillOptions.push({ label: skillName, options: [skill] });
                    skillsSelected.push(skill);
                    section.exportSection = false;
                    break;
                }
                if (desc.includes(`you have proficiency in the ${skill.toLowerCase()} skill`) ||
                    desc.includes(`you gain proficiency in the ${skill.toLowerCase()} skill`)) {
                    skillOptions.push({ label: skillName, options: [skill] });
                    skillsSelected.push(skill);
                }
            }
        }
    }

    static parseLanguages(race: Race | null, languageOptions: ItemOption[], languagesSelected: string[]): void {
        if (!race) {
            return;
        }
        // The only important line in the language text is the first sentance after the term '***Languages.*** '
        /* Know values (first sentence after ):
             You can speak, read, and write Common."
             You can speak, read, and write Common, Darakhul, and a language associated with your Heritage Subrace."
             You can speak, read, and write Common and Draconic."
             You can speak, read, and write Common and Dwarvish."
             You can speak, read, and write Common and Elvish."
             You can speak, read, and write Common and Gnomish."
             You can speak, read, and write Common and Orc."
             You can speak, read, and write Common and Halfling."
             You can speak, read, and write Common and Minotaur."
             You can speak, read, and write Common and Infernal."
             You can speak Erina and either Common or Sylvan."
             You can speak, read, and write Dwarvish and your choice of Common or Undercommon."
             You can speak, read, and write Elvish and your choice of Common or Undercommon."
             You can speak, read, and write Mushroomfolk and your choice of Common or Undercommon."
             You can speak, read, and write Common, Machine Speech (a whistling, clicking language that's incomprehensible to non-gearforged), and a language associated with your Race Chassis."
             You can speak, read, and write Common, Elvish, and one extra language of your choice."
             You can speak, read, and write Common and one extra language of your choice."
             You can speak, read, and write Common and one of the following: Abyssal, Infernal, or Void Speech."
             You can speak, read, and write Common and one other language spoken by your Living Origin."
         */
        const langDetails = (race.languages && race.languages.split('* ')[1]) || '';
        let firstSentance = langDetails.split('.')[0];
        firstSentance = firstSentance.replace(/ ?\([^)]*\)/g, '');
        const literate = firstSentance.toLowerCase().match(/ read,? and write/g);
        firstSentance = firstSentance.replace(/You can speak(,? read)?(,? and write)? /g, '').trim();
        firstSentance = firstSentance.replace(/\.$/g, '').trim();
        const langs = [];
        let found;
        do {
            found = false;
            for (const possibleLang of [...Utils.langs]) {
                if (firstSentance.toLowerCase().startsWith(possibleLang.toLowerCase())) {
                    langs.push(possibleLang);
                    firstSentance = firstSentance.substring(possibleLang.length + 1).trim();
                    firstSentance = firstSentance.replace(/^,? ?(and )?/g, '');
                    found = true;
                }
            }
        } while (found);
        switch (firstSentance) {
            case "a language associated with your Heritage Subrace":
            case "a language associated with your Race Chassis":
                switch (race.slug.replace('-chassis', '').replace('-heritage', '')) {
                    case "derro":         langs.push("Dwarvish");      break;
                    case "dragonborn":    langs.push("Draconic");      break;
                    case "drow":          langs.push("Elvish");        break;
                    case "dwarf":         langs.push("Dwarvish");      break;
                    case "elfshadow-fey": langs.push("Elvish");        break;
                    case "gnome":         langs.push("Gnomish");       break;
                    case "halfling":      langs.push("Halfling");      break;
                    case "human":         langs.push("Any");           break;
                    case "humanhalf-elf": langs.push("Elvish");        break;
                    case "kobold":        langs.push("Draconic");      break;
                    case "ravenfolk":     langs.push("Any");           break;
                    case "tiefling":      langs.push("Infernal");      break;
                    case "trollkin":      langs.push("Machine Speech");break;
                }
                break;
            case "one other language spoken by your Living Origin":
                langs.push("Any"); break;
            case "either Common or Sylvan":
                langs.push("Common or Sylvan"); break;
            case "your choice of Common or Undercommon":
                langs.push("Common or Undercommon"); break;
            case "one extra language of your choice":
                langs.push("Any"); break;
            case "one of the following: Abyssal, Infernal, or Void Speech":
                langs.push("Abyssal or Infernal or Void Speech"); break;
        }
        for (const lang of langs) {
            const langOpts = lang.split(' or ');
            languageOptions.push({ label: race.name, options: langOpts })
            languagesSelected.push((langOpts.length == 1) ? langOpts[0] : '');
        };
        for (const section of race.sections || []) {
            if (section.name === 'Extra Language') {
                languageOptions.push({ label: section.name, options: [...Utils.langs] })
                languagesSelected.push('');
            }
        }
    }

    static parseArmorProficiency(race: Race | null, armorOptions: ItemOption[], armorsSelected: string[]): void {
        for (const section of race?.sections || []) {
            const desc = section.desc.toLowerCase().trim();
            const skillName = race?.name + ' - ' + section.name;
            if (desc === 'you are proficient with one martial weapon of your choice and with light armor.') {
                armorOptions.push({label: section.name, options: ['Light Armor']});
                armorsSelected.push('Light Armor');
            }
            if (desc.includes('you have proficiency with two martial weapons of your choice and with light and medium armor.')) {
                armorOptions.push({label: section.name, options: ['Light Armor']})
                armorsSelected.push('Light Armor');
                armorOptions.push({label: section.name, options: ['Medium Armor']})
                armorsSelected.push('Medium Armor');
            }
        }
    }
    static parseWeaponProficiency(race: Race | null, weaponOptions: ItemOption[], weaponsSelected: string[]): void {
        const martialWeaponNames = Abilities.weapons.filter(ab => ab.weapon_data?.simple === false).map(ab => ab.name);
        for (const section of race?.sections || []) {
            const desc = section.desc.toLowerCase().trim();
            const skillName = race?.name + ' - ' + section.name;
            if (desc.includes('you are proficient with one martial weapon of your choice')) {
                weaponOptions.push({label: section.name, options: martialWeaponNames});
                weaponsSelected.push('');
            }
            if (desc.includes('you have proficiency with two martial weapons of your choice') || 
                desc.includes('and you are proficient with two martial weapons of your choice')) {
                weaponOptions.push({label: section.name, options: martialWeaponNames});
                weaponOptions.push({label: section.name, options: martialWeaponNames});
                weaponsSelected.push('');
                weaponsSelected.push('');
            }
            if (desc.includes('you have proficiency with the battleaxe, handaxe, light hammer, and warhammer.')) {
                weaponOptions.push({label: section.name, options: ['Battleaxe']});
                weaponsSelected.push('Battleaxe');
                weaponOptions.push({label: section.name, options: ['Handaxe']});
                weaponsSelected.push('Handaxe');
                weaponOptions.push({label: section.name, options: ['Light Hammer']});
                weaponsSelected.push('Light Hammer');
                weaponOptions.push({label: section.name, options: ['Warhammer']});
                weaponsSelected.push('Warhammer');
            }
            if (desc.includes('you have proficiency with the longsword, shortsword, shortbow, and longbow')) {
                weaponOptions.push({label: section.name, options: ['Longsword']});
                weaponsSelected.push('Longsword');
                weaponOptions.push({label: section.name, options: ['Shortsword']});
                weaponsSelected.push('Shortsword');
                weaponOptions.push({label: section.name, options: ['Shortbow']});
                weaponsSelected.push('Shortbow');
                weaponOptions.push({label: section.name, options: ['Longbow']});
                weaponsSelected.push('Longbow');
            }
            if (desc.includes('you have proficiency with spears and shortbows.')) {
                weaponOptions.push({label: section.name, options: ['Spear']});
                weaponsSelected.push('Spear');
                weaponOptions.push({label: section.name, options: ['Shortbow']});
                weaponsSelected.push('Shortbow');
            }
        }
    }

    static parseSpeed(race: any): Speed {
        const speed = new Speed();
        if (race?.speed) {
            Object.assign(speed, race.speed);
            if (race.slug === "drow" || race.slug === "purified" || race.slug === "fever-bit" || race.slug === "delver") {
                speed.walk = 30; // fix error in API
            }
            if (race.slug === "gnome-chassis" || race.slug === "dwarf-chassis" ||
                race.slug === "gnome-heritage" || race.slug === "dwarf-heritage" || race.slug === "halfling-heritage") {
                speed.walk = 25;
            }
            if (race.traits && race.traits.includes("You have a swimming speed of 25 feet.")) {
                speed.swim = 25;
            }
        }
        return speed;
    }

    static parseSize(race: any): string {
        if (race?.name === "gnome-chassis" || race?.name === "gnome-heritage" || race?.name === "halfling-heritage") {
            return "Small";
        }
        return race?.size_raw || '';
    }


    static parseTraitsToSections(traits: string): Section[] {
        const sections: Section[] = [];
        const lines = traits.split(/ ?\n ?\n/g);
        lines.map(t => t.split(/[_*][_*]+/))
            .forEach(a => {
                if (a.length > 2) {
                    let name = a[1];
                    if (name.endsWith('.')) {
                        name = name.substring(0, name.length - 1);
                    }
                    sections.push(new Section(name, a[2].trim(), undefined, undefined, undefined, null));
                } else if (a[0].trim()) {
                    sections.push(new Section('', a[0].trim(), undefined, undefined, undefined, null));
                }
            });
        return sections;
    };

    // parse all the race traits into sections:
    static parseSections(race: any): void {
        if (!race.traits) return;

        race.traits = race.traits.replace(/\*\* *\n *\n/g, '**\n');
        race.traits = race.traits.replace(/ \n \n/g, '\n');
        const sections = RaceService.parseTraitsToSections(race.traits);
        race.sections = [];
        let lastSection = null;
        const mapByOptionName = new Map();
        for (const section of sections) {
            if (lastSection && section.depth === lastSection.depth + 1) {
                lastSection.children.push(section);
                // if (lastSection.name === 'Fighting Style') {
                //     lastSection.options.push(section.name);
                // }
            } else {
                race.sections.push(section);
                lastSection = section;
                mapByOptionName.set(section.name, section);
                if (section.name === 'Extra Language') {
                }
                if (race.name === 'Dragonborn') {
                    let raceTableDesc = '';
                    for (const section2 of sections) {
                        if (section2.name === 'Draconic Ancestry') {
                            if (section2.desc.includes('--|--')) {
                                raceTableDesc = section2.desc;
                            }
                        }
                    }
                    if (section.name === 'Draconic Ancestry') {
                        if (section.desc.includes('--|--')) {
                            section.exportSection = false;
                            const rows = section.desc.split('\n');
                            const options = [];
                            for (let row = 0; row < rows.length; row++) {
                                if (row > 1) {
                                    const cells = rows[row].split(/\s*\|\s*/);
                                    options.push(cells[1]);
                                }
                            }
                            section.options = options;
                            section.selectedOption = '';

                            section.fnDisplaySection = (optionsSelectedArrayByOptionNameMap: Map<string, string[]>) => {
                                const optionSelectedArray = optionsSelectedArrayByOptionNameMap.get(section.name.trim());
                                if (!optionSelectedArray) {
                                    return section;
                                }
                                const draconicAncestryData = CharacterService.getTableRow(raceTableDesc, optionSelectedArray[0]);
                                const descArray = [];
                                for (const data of draconicAncestryData || []) {
                                    descArray.push(`>**${data.name}**: ${data.desc}`);
                                }
                                return new Section(`${optionSelectedArray[0]} Draconic Ancestry`, descArray.join('\n'), undefined, undefined, undefined, null);
                            }
                        } else {
                            section.fnDisplaySection = (optionsSelectedArrayByOptionNameMap: Map<string, string[]>) => {
                                const optionSelectedArray = optionsSelectedArrayByOptionNameMap.get(section.name.trim());
                                const draconicRace = optionSelectedArray && optionSelectedArray[0];
                                if (!draconicRace) {
                                    return section;
                                }
                                return null;
                            }
                        }
                    } else if (section.name === 'Breath Weapon') {
                        section.fnDisplaySection = (optionsSelectedArrayByOptionNameMap: Map<string, string[]>) => {
                            const optionSelectedArray = optionsSelectedArrayByOptionNameMap.get('Draconic Ancestry');
                            const draconicRace = optionSelectedArray && optionSelectedArray[0];
                            if (!draconicRace) {
                                return section;
                            }
                            let desc = section.desc;
                            desc = desc.replace('You can use your action to exhale destructive energy. ', '');
                            desc = desc.replace('Your draconic ancestry determines the size, shape, and damage type of the exhalation.\n', '');
                            const draconicAncestryData = CharacterService.getTableRow(raceTableDesc, draconicRace);
                            let damageType = '';
                            let area = '';
                            let save = '';
                            for (const data of draconicAncestryData || []) {
                                if (data.name === 'Damage Type') {
                                    damageType = data.desc;
                                }
                                if (data.name === 'Breath Weapon') {
                                    area = data.desc.split('(')[0].trim();
                                    save = data.desc.split('(')[1].split('.')[0].replace('(', '').trim();
                                    break;
                                }
                            }
                            desc = desc.replace('the area of the exhalation', 'a ' + area);
                            desc = desc.replace('saving throw, the type of which is determined by your draconic ancestry', save + ' saving throw');
                            desc = desc.replace('. The DC for this saving throw equals 8 + your Constitution modifier + your proficiency bonus. ', ' at a DC [CON SAVE]. ');
                            desc = desc.replace(' 2d6 damage', ` [2d6] ${damageType} damage`);
                            return new Section(section.name, desc, undefined, undefined, undefined, null);
                        }
                    } else if (section.name === 'Damage Resistance') {
                        section.fnDisplaySection = (optionsSelectedArrayByOptionNameMap: Map<string, string[]>) => {
                            const optionSelectedArray = optionsSelectedArrayByOptionNameMap.get('Draconic Ancestry');
                            const draconicRace = optionSelectedArray && optionSelectedArray[0];
                            if (!draconicRace) {
                                return section;
                            }
                            if (section.desc.includes('You have resistance to the damage type associated with your draconic ancestry.')) {
                                const draconicAncestryData = CharacterService.getTableRow(raceTableDesc, draconicRace);
                                let desc = section.desc;
                                for (const data of draconicAncestryData || []) {
                                    if (data.name === 'Damage Type') {
                                        desc = `You have resistance to ${data.desc}.`;
                                        break;
                                    }
                                }
                                return new Section(section.name, desc, undefined, undefined, undefined, null);
                            }
                            return null;
                        }
                        section.fnExportSection = (optionsSelectedArrayByOptionNameMap: Map<string, string[]>, damageResistancesArray: string[]) => {
                            const optionSelectedArray = optionsSelectedArrayByOptionNameMap.get('Draconic Ancestry');
                            const draconicRace = optionSelectedArray && optionSelectedArray[0];
                            if (draconicRace) {
                                const draconicAncestryData = CharacterService.getTableRow(section.desc, draconicRace);
                                for (const data of draconicAncestryData || []) {
                                    if (data.name === 'Damage Type') {
                                        damageResistancesArray.push(data.desc);
                                        section.exportSection = false;
                                        break;
                                    }
                                }
                            }
                        }
                    }
                } else {
                    // We should export this section
                    section.exportSection = true;
                }
            }
        }
    };

    static processRace = (race: Race | null, optionsSelectedArrayByOptionNameMap: Map<string, string[]>, sections: Section[]) => {
        if (race) {
            if (race.speed_desc && race.speed_desc.includes('Your speed is not reduced by wearing heavy armor.')) {
                sections.push(new Section('Speed', 'Your speed is not reduced by wearing heavy armor.', undefined, undefined, undefined, null));
            }

            race.sections?.forEach(section => {
                const display = section.fnDisplaySection && section.fnDisplaySection(optionsSelectedArrayByOptionNameMap);
                if (display) {
                    sections.push(display);
                } else if (section.exportSection) {
                    sections.push(section);
                    section.children?.forEach(c => {
                        sections.push(c);
                    });
                }
            });
        }
    }

    static async fetchRaces(cacheOnly: boolean, progCallback?: (data: any[], totalCount: number) => void): Promise<Race[]> {
        try {
            const raceObjs: any[] = await ApiService.getAllRaces(cacheOnly, progCallback);
            let races: Race[] = [];
            for (const raceObj of raceObjs) {
                const race: Race = {
                    name: '', slug: '', desc: '', asi: [], asi_desc: '', traits: '',
                    document__slug: '', document__title: '', document__license_url: '', document__url: ''
                };
                Object.assign(race, raceObj);
                races.push(race);
            }
            races = RaceService.expandSubRaces(races);
            races.forEach(race => this.parseSections(race));
            return races;
        } catch (error) {
            console.error("Failed to fetch races", error);
            return [];
        }
    }
}
export default RaceService;